// client/src/components/auth/RequireResource.jsx
import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Alert, Button, CircularProgress, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import {
  canAccessPanel,
  getSystemRole,
  hasAdminPanel,
  hasGameAction,
  hasRoute,
  isAccessReady,
  hasRole,
} from '../../utils/accessControl';

// ============================================================================
// Внутренний компонент загрузки
// ============================================================================

function AccessLoading() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Загрузка прав доступа...
      </Typography>
    </Box>
  );
}

// ============================================================================
// Внутренний компонент отказа
// ============================================================================

function AccessDenied({ resourceLabel, systemRole }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        gap: 2,
        p: 3,
      }}
    >
      <Alert severity="error" sx={{ maxWidth: 500 }}>
        🚫 Доступ запрещён. Требуется право «{resourceLabel}».
        <br />
        Ваша роль: <strong>{systemRole || 'не определена'}</strong>.
      </Alert>
      <Button variant="contained" href="/">
        На главную
      </Button>
    </Box>
  );
}

// ============================================================================
// RequireResource — проверка конкретного admin_panel (обратная совместимость)
// ============================================================================

/**
 * Проверка прав на конкретный admin_panel.
 *
 * ⚠️ БЕЗ useAccessControl — только useSelector + чистые функции.
 * Это исключает React #300.
 */
export default function RequireResource({ resource, children }) {
  const auth = useSelector((state) => state.auth);

  const ready = isAccessReady(auth);
  const allowed = ready && canAccessPanel(auth, resource);
  const systemRole = getSystemRole(auth);

  if (!ready) return <AccessLoading />;
  if (!allowed) return <AccessDenied resourceLabel={resource} systemRole={systemRole} />;
  return children;
}

// ============================================================================
// RequireAdminPanel — алиас для RequireResource (семантическое имя)
// ============================================================================

/**
 * Проверка admin_panel по имени (`users`, `ontologies`, `rules`, `services`, `assets`).
 * Эквивалент `RequireResource`, но с явным именем пропса.
 */
export function RequireAdminPanel({ name, children, fallback = null }) {
  const auth = useSelector((state) => state.auth);

  const ready = isAccessReady(auth);
  const allowed = ready && hasAdminPanel(auth, name);

  if (!ready) return <AccessLoading />;
  if (!allowed) {
    if (fallback !== null) return fallback;
    return <AccessDenied resourceLabel={`панель «${name}»`} systemRole={getSystemRole(auth)} />;
  }
  return children;
}

// ============================================================================
// RequireRoute — проверка route по пути (`/designer`, `/worlds`, ...)
// ============================================================================

export function RequireRoute({ path, children, fallback = null }) {
  const auth = useSelector((state) => state.auth);

  const ready = isAccessReady(auth);
  const allowed = ready && hasRoute(auth, path);

  if (!ready) return <AccessLoading />;
  if (!allowed) {
    if (fallback !== null) return fallback;
    return <AccessDenied resourceLabel={`маршрут «${path}»`} systemRole={getSystemRole(auth)} />;
  }
  return children;
}

// ============================================================================
// RequireGameAction — проверка game_action (`observe_others`, `emancipate`, ...)
// ============================================================================

export function RequireGameAction({ action, children, fallback = null }) {
  const auth = useSelector((state) => state.auth);

  const ready = isAccessReady(auth);
  const allowed = ready && hasGameAction(auth, action);

  if (!ready) return <AccessLoading />;
  if (!allowed) {
    if (fallback !== null) return fallback;
    return <AccessDenied resourceLabel={`действие «${action}»`} systemRole={getSystemRole(auth)} />;
  }
  return children;
}

// ============================================================================
// RequireRole — проверка роли (user/curator/admin/superadmin)
// ============================================================================

/**
 * Проверка, что текущая роль входит в `roles`.
 *
 * @param {object} props
 * @param {string[]} props.roles — например `["admin", "superadmin"]`
 * @param {React.ReactNode} props.children
 * @param {React.ReactNode} [props.fallback] — по умолчанию `<Navigate to="/" />`
 * @param {boolean} [props.waitForAccess] — ждать ли `accessChecked` (по умолчанию `true`)
 */
export function RequireRole({ roles, children, fallback = null, waitForAccess = true }) {
  const auth = useSelector((state) => state.auth);

  const ready = waitForAccess ? isAccessReady(auth) : true;

  if (!ready) return <AccessLoading />;

  const allowed = hasRole(auth, roles);

  if (!allowed) {
    if (fallback !== null) return fallback;
    return <Navigate to="/" replace />;
  }

  return children;
}